import {
  canOpenMyBoothShare,
  canAccessMarketplaceOperatorShell,
  isIndependentMarketplaceOperator,
  MARKETPLACE_STAFF_ONLY_ROLES,
} from "./marketplaceOperatorAccess";
import { getMarketplaceOperatorHubPath } from "./marketplaceOperatorRoutes";

const MARKETPLACE_ACCOUNT_ROLES = new Set([
  "independent_professional",
]);
const MARKETPLACE_SHARED_PATH_PREFIXES = [
  "/search",
  "/account/rentals",
  "/account/payments",
  "/account/profile",
  "/workspaces/",
  "/checkout/",
];

export function isMarketplaceAccountRole(role = "") {
  return MARKETPLACE_ACCOUNT_ROLES.has(String(role || "").toLowerCase());
}

export function getMarketplaceSuperAdminLandingPath() {
  return "/superadmin";
}

export function getMarketplaceAccessNeededPath({
  role = "",
  source = "myboothshare_access_gate",
} = {}) {
  const params = new URLSearchParams();
  const normalizedRole = String(role || "").trim().toLowerCase();
  if (normalizedRole) {
    params.set("role", normalizedRole);
  }
  if (source) {
    params.set("source", source);
  }
  const query = params.toString();
  return `/access-needed${query ? `?${query}` : ""}`;
}

export function getMarketplaceAuthenticatedLandingPath({
  role = "",
  user = null,
} = {}) {
  const normalizedRole = String(role || user?.role || "").toLowerCase();

  if (Boolean(user?.is_super_admin)) {
    return getMarketplaceSuperAdminLandingPath();
  }

  if (canAccessMarketplaceOperatorShell({ role: normalizedRole, user })) {
    return getMarketplaceOperatorHubPath();
  }

  if (
    canOpenMyBoothShare({ role: normalizedRole, user }) &&
    (isMarketplaceAccountRole(normalizedRole) || isIndependentMarketplaceOperator(user))
  ) {
    return "/account/rentals";
  }

  if (MARKETPLACE_STAFF_ONLY_ROLES.has(normalizedRole)) {
    return getMarketplaceAccessNeededPath({ role: normalizedRole });
  }

  return "/search";
}

export function getMarketplaceCrossLaunchPath({
  isAuthenticated = false,
  role = "",
  user = null,
} = {}) {
  if (!isAuthenticated) {
    return "/search";
  }

  return getMarketplaceAuthenticatedLandingPath({ role, user });
}

export function getMarketplacePostSalonSwitchPath({
  pathname = "",
  search = "",
  role = "",
  user = null,
} = {}) {
  const normalizedPath = String(pathname || "");
  const normalizedSearch = String(search || "");
  const currentPath = `${normalizedPath}${normalizedSearch}`;

  if (!normalizedPath || normalizedPath === "/" || normalizedPath === "/select-salon") {
    return getMarketplaceAuthenticatedLandingPath({ role, user });
  }

  if (normalizedPath.startsWith("/account/operator")) {
    return canAccessMarketplaceOperatorShell({ role, user })
      ? currentPath
      : getMarketplaceAuthenticatedLandingPath({ role, user });
  }

  if (normalizedPath.startsWith("/account/") && !canOpenMyBoothShare({ role, user })) {
    return getMarketplaceAccessNeededPath({ role: role || user?.role });
  }

  if (normalizedPath === "/account/bookings" || normalizedPath.startsWith("/account/bookings/")) {
    return `/account/rentals${normalizedSearch}`;
  }

  const canStayOnCurrentPath = MARKETPLACE_SHARED_PATH_PREFIXES.some((prefix) =>
    prefix.endsWith("/") ? normalizedPath.startsWith(prefix) : normalizedPath === prefix
  );

  if (canStayOnCurrentPath) {
    return currentPath;
  }

  return getMarketplaceAuthenticatedLandingPath({ role, user });
}
